import { useMemo } from 'react'

import { useAuth } from '@/auth/use-auth'
import { PERMISSIONS } from '@/lib/permissions'

/**
 * The permission codes the group endpoints authorise against, and the codes that countersign the
 * dual-controlled ones.
 *
 * All of them are in the generated catalogue, so nothing here is a hand-written guess — which
 * matters, because the `.approve` codes are what let a screen say whether an action is going to
 * complete or go into a queue before somebody clicks it.
 */
export const GROUP_PERMISSIONS = {
  view: PERMISSIONS.groups.view,
  create: PERMISSIONS.groups.create,
  createApprove: PERMISSIONS.groups.createApprove,
  update: PERMISSIONS.groups.update,
  memberAdd: PERMISSIONS.groups.memberAdd,
  memberExit: PERMISSIONS.groups.memberExit,
  leaderChange: PERMISSIONS.groups.leaderChange,
  leaderChangeApprove: PERMISSIONS.groups.leaderChangeApprove,
  officerAssign: PERMISSIONS.groups.officerAssign,
  officerAssignApprove: PERMISSIONS.groups.officerAssignApprove,
  locationManage: PERMISSIONS.groups.locationManage,
  locationManageApprove: PERMISSIONS.groups.locationManageApprove,
  meetingSchedule: PERMISSIONS.groups.meetingSchedule,
  meetingRecord: PERMISSIONS.groups.meetingRecord,
  healthView: PERMISSIONS.groups.healthView,
  healthAttest: PERMISSIONS.groups.healthAttest,
  healthAttestApprove: PERMISSIONS.groups.healthAttestApprove,
} as const

export interface GroupPermissions {
  canView: boolean
  canCreate: boolean
  canUpdate: boolean
  canAddMember: boolean
  canExitMember: boolean
  canChangeLeader: boolean
  canAssignOfficer: boolean
  canManageLocation: boolean
  canScheduleMeetings: boolean
  canRecordAttendance: boolean
  canViewHealth: boolean
  /** Attesting a scoreless group's standing — the maker's half of the cold-start judgement. */
  canAttestHealth: boolean
  /**
   * Whether this user also holds the countersigning half of each dual-controlled action.
   *
   * This is what lets a dialog say "this will be sent for a countersignature" before the click
   * instead of after it. It is a statement about the *user*, never about the outcome: the approval
   * policy is the server's, it can require a second person even from someone holding both halves,
   * and the answer the screen reports afterwards comes from `awaitingApproval` on the response and
   * from nothing else.
   */
  canCountersignCreate: boolean
  canCountersignLeaderChange: boolean
  canCountersignOfficerAssign: boolean
  canCountersignLocationChange: boolean
  canCountersignHealthAttest: boolean
}

/**
 * What this user may do with a group. Presentation only: the server authorises every command
 * against the live catalogue, so a visible button is not an open door and a hidden one is not a
 * closed door.
 */
export function useGroupPermissions(): GroupPermissions {
  const { user } = useAuth()
  const permissions = user?.permissions

  return useMemo(() => {
    const holds = (code: string) => permissions?.some((held) => held === code) ?? false

    return {
      canView: holds(GROUP_PERMISSIONS.view),
      canCreate: holds(GROUP_PERMISSIONS.create),
      canUpdate: holds(GROUP_PERMISSIONS.update),
      canAddMember: holds(GROUP_PERMISSIONS.memberAdd),
      canExitMember: holds(GROUP_PERMISSIONS.memberExit),
      canChangeLeader: holds(GROUP_PERMISSIONS.leaderChange),
      canAssignOfficer: holds(GROUP_PERMISSIONS.officerAssign),
      canManageLocation: holds(GROUP_PERMISSIONS.locationManage),
      canScheduleMeetings: holds(GROUP_PERMISSIONS.meetingSchedule),
      canRecordAttendance: holds(GROUP_PERMISSIONS.meetingRecord),
      canViewHealth: holds(GROUP_PERMISSIONS.healthView),
      canAttestHealth: holds(GROUP_PERMISSIONS.healthAttest),
      canCountersignCreate: holds(GROUP_PERMISSIONS.createApprove),
      canCountersignLeaderChange: holds(GROUP_PERMISSIONS.leaderChangeApprove),
      canCountersignOfficerAssign: holds(GROUP_PERMISSIONS.officerAssignApprove),
      canCountersignLocationChange: holds(GROUP_PERMISSIONS.locationManageApprove),
      canCountersignHealthAttest: holds(GROUP_PERMISSIONS.healthAttestApprove),
    }
  }, [permissions])
}
